//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import DataSource from 'devextreme/data/data_source'

//#endregion

import { Enums } from '../../../models/common/enums'
import { IUserSession } from '../../../helpers/user-session'
import { Activity } from '../../../models/common/activity'
import { LocalDictionaryFactory } from '../../api/api.module'
import { LoggerService } from '../../common/common.module'
import { ColumnModel } from '../../api/api.models'
import { EventsNames } from '../../../helpers/events-names'
import { OperatorLayoutSettings } from '../../../helpers/operator-layout-settings'
import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsOptions, DetailsPopupOptions } from '../../common/details-popup/details-popup.directive'
import { GridOptions, GridTypes } from '../../common/grid/grid.controller'
import { HistorySource } from '../../common/history/history.directive.controller'
import { TemplateHelper, ConfigHelper, LocalizationHelper, DataTypeGroupComponent, TypeHelper, ConvertHelper } from '../../../helpers'
import { ActionService } from '../../../services/action'
import { OpAction, OpDataType, OpDataTypeInGroup } from '../../../models/core'
import { DetailsParametersOptions } from '../../../modules/common/details-parameters/details-parameters.directive'
import { OpActionPacket, OpPacketDataArch, OpPacketStatus } from '../../../models/daq/'
import { TreeListOptions } from '../../../modules/common/tree-list'
import { DataProviderRequest } from '../../../data-provider'
import { TreeListController } from '../../../modules/common/tree-list/tree-list.controller'

interface ActionsDetailsScope extends DetailsScope {
    ACTION_COMMON_TAB_NAME: string
    ACTION_HISTORY_TAB_NAME: string
    ACTION_PARAMS_ACCORDION_NAME: string
    ACTION_ADDITIONAL_PARAMS_ACCORDION_NAME: string
    ACTION_PACKETS_ACCORDION_NAME: string
    NO_ACTIVE_ACCORDIONS_NAME: string
    NO_DATA_TO_SHOW: string
    EDIT_NAME: string

    actionPacketsGridOptions: GridOptions
    detailsParametersOptions: DetailsParametersOptions
    detailedParamGridOptions: TreeListOptions

    deviceAnswerGridOptions: GridOptions
    actionHistoryOptions: HistorySource
    isPackedDataLoading: boolean
}

export class ActionsDetailsDirectiveController extends Details<ActionsDetailsScope> {
    //#region Consts

    private get ACTION_COMMON_TAB_NAME(): string { return 'common' }
    private get ACTION_HISTORY_TAB_NAME(): string { return 'history' }
    private get ACTION_PARAMS_ACCORDION_NAME(): string { return 'params' }
    private get ACTION_ADDITIONAL_PARAMS_ACCORDION_NAME(): string { return 'additional' }
    private get ACTION_PACKETS_ACCORDION_NAME(): string { return 'packets' }
    private get NO_DATA_TO_SHOW(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoData) }
    private get NO_ACTIVE_ACCORDIONS_NAME(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoActiveAccordions) }
    private get EDIT_NAME(): string { return LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit) }
    private get ACTION_DEVICE_ANSWER_GRID_NAME(): string { return 'actionDevicesAnswerGrid' }
    private get ACTION_PACKETS_GRID_NAME(): string { return 'actionPacketsGrid' }
    private get ACTION_DETALS_GRID_NAME(): string { return 'actionDetailsGrid' }

    //#endregion

    private getActionRequest: DataProviderRequest<OpAction> = null
    private referenceObjectId: number
    private referenceObject: OpAction
    private deviceAnswerGrid: DevExpress.ui.dxDataGrid
    private detailedParamGrid: TreeListController<OpAction>

    constructor(
        protected $scope: ActionsDetailsScope,
        protected $q: ng.IQService,
        protected $timeout: ng.ITimeoutService,
        protected logger: LoggerService,
        protected localDictionary: LocalDictionaryFactory,
        protected userSession: IUserSession,
        private actionService: ActionService,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $q, logger, localDictionary, OperatorLayoutSettings.DevicePopupTabActive, userSession, referenceType)

        this.setEvents()
        this.setOptions()
        this.setTemplateConsts()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.ACTION_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    private updatePacketsData(packets: OpActionPacket[]): void {
        this.$scope.deviceAnswerGridOptions = null
        this.$timeout(() => {

            $.when(
                this.actionService.getActionDevicesAnswerColumns(),
                this.actionService.getActionPacketDataColumns()
            )
                .then((columns: ColumnModel[], packetDataColumns: ColumnModel[]) => {
                    this.$scope.deviceAnswerGridOptions = {
                        name: this.ACTION_DEVICE_ANSWER_GRID_NAME,
                        type: GridTypes.NO_SEARCH_BAR_SMALL,
                        allowColumnReordering: !this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.ActionPackets),
                        columnsDictionary: columns,
                        dataSource: packets,
                        buttonsOptions: {
                            enabled: false,
                        },
                        onInitialized: (e: any) => {
                            this.deviceAnswerGrid = e.component
                        },

                        onCellPrepared: (options: any) => {
                            this.onActionDevicesAnswerGridCellPrepared(options)
                        },

                        dataSourceParser: (item: OpActionPacket) => {
                            return this.getAnswerDeviceDataSourceParser(item, packetDataColumns)
                        },

                        masterDetail: {
                            enabled: true,
                            template: "detail"
                        }
                    }
                })

        })
    }

    private getAnswerDeviceDataSourceParser(item: OpActionPacket, packetDataColumns: ColumnModel[]): { sourceObject?: OpPacketDataArch, additionalFields?: { key: string, value: any }[] } {
        const result: {
            sourceObject?: any
            additionalFields?: { key: string, value: any }[]
        } = {}

        result.sourceObject = item
        result.additionalFields = (item.packet == null)
            ? []
            : [{
                key: this.actionService.idColumnName,
                value: item.packet.idPacket
            }, {
                key: this.actionService.addresColumnName,
                value: item.packet.address
            },
            {
                key: this.actionService.isIncomingColumnName,
                value: item.packet.isIncoming
            },
            {
                key: this.actionService.lastUpdateColumnName,
                value: item.packet.lastUpdate
            },
            {
                key: this.actionService.serialNbrColumnName,
                value: item.packet.serialNbr
            },
            {
                key: this.actionService.transmissionDriverColumnName,
                value: (item.packet.transmissionDriver != null) ? item.packet.transmissionDriver.toString() : null
            },
            {
                key: this.actionService.packetStatusColumnName,
                value: item.packet.packetStatus
            },
            {
                key: 'masterDetailOptions',
                value: (_.size(item.packet.packetData) > 0)
                    ? this.getActionPacketsGridOptions(item.packet.packetData, packetDataColumns)
                    : null
            }]

        return result
    }

    private getActionPacketsGridOptions(packetData: OpPacketDataArch[], packetDataColumns: ColumnModel[]): GridOptions {
        return <GridOptions>{
            dataSource: packetData,
            dataSourceParser: (item: OpPacketDataArch) => {
                return this.getActionPackedDataSourceParser(item)
            },
            name: this.ACTION_PACKETS_GRID_NAME,
            type: GridTypes.SMALL,
            buttonsOptions: {
                enabled: false
            },
            columnsDictionary: packetDataColumns,
            onInitialized: (e: any) => {
                this.deviceAnswerGrid.updateDimensions()
                e.component.updateDimensions()
            },
            onCellPrepared: (options: any) => {
                this.onDeviceAnswerDetailCellPrepared(options)
            }
        }
    }

    private getActionPackedDataSourceParser(item: OpPacketDataArch): { sourceObject?: OpPacketDataArch, additionalFields?: { key: string, value: any }[] } {
        const result: {
            sourceObject?: OpPacketDataArch
            additionalFields?: { key: string, value: any }[]
        } = {}

        result.sourceObject = item
        const dataType: OpDataType = this.userSession.getDataProvider().DataType.getDataTypeLocal(item.idDataType)

        result.additionalFields =
            [{
                key: this.actionService.dataTypeColumnName,
                value: (dataType == null) ? item.name : dataType.toString()
            }, {
                key: this.actionService.valueColumnName,
                value: ConvertHelper.prepareDataValue(item.value, dataType)
            }]

        return result
    }

    //#region Setup

    public getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ACTION_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ActionParameters)) {
            const items: DetailsAccordionSection[] = []

            items.push({
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ActionData),
                canEdit: false,
                name: this.ACTION_PARAMS_ACCORDION_NAME,
                showStatus: false,
                visible: true
            })
            items.push({
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ActionParams),
                canEdit: false,
                name: this.ACTION_ADDITIONAL_PARAMS_ACCORDION_NAME,
                showStatus: false,
                visible: true
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ActionDetailsAccordions, 0, items))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ACTION_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ActionPackets) &&
            this.userSession.getLoggedOperator().hasPermission(Activity.PACKET_LIST)) {
            const items: DetailsAccordionSection[] = []
            items.push({
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ActionDeviceAnswer),
                canEdit: false,
                name: this.ACTION_PACKETS_ACCORDION_NAME,
                showStatus: false,
                visible: true
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.ActionDetailsPackets, 1, items))
        }

        return accordions
    }

    public getTabs(): { template: string, name: string }[] {
        const tabs: { template: string, name: string }[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ACTION_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ActionParameters) ||
            (!this.userSession.getLoggedOperator().hasPermission(Activity.ACTION_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ActionPackets) &&
                this.userSession.getLoggedOperator().hasPermission(Activity.PACKET_LIST))) {
            tabs.push({
                template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common), ConfigHelper.Icon.HOME, this.userSession),
                name: this.ACTION_COMMON_TAB_NAME
            })
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ACTION_MODULE_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.ActionHistory)) {
            tabs.push({
                template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.History), ConfigHelper.Icon.HISTORY, this.userSession),
                name: this.ACTION_HISTORY_TAB_NAME
            })
        }

        return tabs
    }

    //#endregion

    //#region Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options

        this.referenceObjectId = options.referenceObjectId

        this.onClear()
        this.showPopup()
        this.onRefresh()
    }

    private onClose(): void {
        this.hidePopup()
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => {
                this.onOpen(args[3])
            }
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )
    }

    private onClear(): void {
        if (this.getActionRequest != null) {
            this.getActionRequest.cancel()
        }

        this.$scope.popupTitle = TypeHelper.emptyString
        this.$scope.showTabs = false
        this.referenceObject = null

        this.$scope.detailsParametersOptions = null
        this.$scope.detailedParamGridOptions = null
        this.$scope.deviceAnswerGridOptions = null
    }

    private onRefresh(toRefresh: OpAction = null): void {
        this.$scope.options.isDataLoaded = false

        const deferred: JQueryDeferred<OpAction> = $.Deferred<OpAction>()

        if (toRefresh == null) {
            this.getActionRequest = this.actionService.getAction(this.referenceObjectId)

            this.getActionRequest
                .then((result: OpAction) => {
                    this.$scope.popupTitle = result.toString()

                    deferred.resolve(result)
                })
                .fail((error: any) => {
                    deferred.resolve(null)
                })
                .always(() => {
                    this.$scope.showTabs = true
                    this.setHistoryOptions()
                })
        } else {
            deferred.resolve(toRefresh)
        }

        deferred.promise()
            .then((result: OpAction) => {
                if (result != null && (!this.referenceObject || this.referenceObject.idAction == result.idAction)) {
                    this.referenceObject = result

                    return $.when(
                        this.setDetalsParametersOptions(),
                        this.setDetailedParamGridOptions(),

                        this.updatePacketData()
                    )
                }
            }).always(() => {
                this.$scope.options.isDataLoaded = true
            })
    }

    private onDeviceAnswerDetailCellPrepared(options: any): void {
        if (options.rowType == "data") {
            const fieldData: string = options.value
            const fieldColumnName: string = options.column.dataField

            //CUSTOM CELL FOR STATUS
            if (fieldData && fieldColumnName == this.actionService.statusColumnName) {
                if (fieldData.toUpperCase() == "OK") { //OK comes from WS
                    options.cellElement.css('background-color', this.actionService.greenColor);
                } else {
                    options.cellElement.css('background-color', this.actionService.redColor);
                }

                const fieldHtml: string = `<span>${fieldData}</span>`;
                options.cellElement.html(fieldHtml);
            }
        }
    }

    private onActionDevicesAnswerGridCellPrepared(options: any): void {
        const fieldData: any = options.value
        const fieldColumnName: string = options.column.dataField

        if (options.rowType == "data") {
            //CUSTOM CELL FOR STATUS
            if (fieldData != null && fieldColumnName == this.actionService.packetStatusColumnName) {
                if ((<OpPacketStatus>fieldData).idPacketStatus === Enums.PacketStatus.Delivered) {
                    options.cellElement.css('background-color', this.actionService.greenColor)
                } else {
                    options.cellElement.css('background-color', this.actionService.redColor)
                }

                const fieldHtml: string = `<span>${fieldData}</span>`
                options.cellElement.html(fieldHtml)
            }

            //CUSTOM CELL FOR DIRECTION
            if (fieldColumnName == this.actionService.isIncomingColumnName) {
                if (fieldData == true) {
                    options.cellElement.css('color', this.actionService.greenColor);
                }
                if (fieldData == false) {
                    options.cellElement.css('color', this.actionService.redColor);
                }
            }
        }
    }

    //#endregion

    // Options

    private setOptions(): void {
        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,

            title: LocalizationHelper.getLocalizedText('ActionDetails', { collection: "Popup" }),
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),

            getAccordionDataMethod: () => { return this.getAccordionData() },
            getAccordionSelectionMethod: () => { return this.getAccordionSelection() },
            updateAccordionMethod: (names: any[]) => { return this.updateAccordions(names) }
        }
    }

    private setHistoryOptions(): void {
        this.$scope.actionHistoryOptions = {
            dataMethodName: null, // DO USUNIĘCIA
            id: null, // DO USUNIĘCIA
            name: 'actionsHistory',
            referenceType: this.referenceType,
            referenceValue: this.$scope.modelId,

            allowColumnReordering: !this.userSession
                .getLoggedOperator()
                .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.ActionHistory),
        }
    }

    private setDetalsParametersOptions(): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()
        this.$scope.detailsParametersOptions = null

        DataTypeGroupComponent
            .getDataTypeInGroupByModule(
                this.userSession.getDataProvider(),
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
                this.moduleName
            )
            .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                this.$scope.detailsParametersOptions = {
                    dataTypes: dataTypeInGroup,
                    referenceType: this.referenceType,
                    referenceObject: this.referenceObject,
                    useDynamicValues: true,
                    onDataLoaded: () => {
                        def.resolve()
                    }
                }
            })
            .catch(() => {
                def.reject()
            })

        if (this.isAccordionHidden(this.$scope.ACTION_PARAMS_ACCORDION_NAME)) {
            def.resolve()
        }

        if (this.isAccordionCollapsed(this.$scope.ACTION_PARAMS_ACCORDION_NAME)) {
            def.resolve()
        }

        return def.promise()
    }

    private setDetailedParamGridOptions(): void {
        this.$scope.detailedParamGridOptions = {
            name: this.ACTION_DETALS_GRID_NAME,
            type: GridTypes.NO_SEARCH_BAR_SMALL,
            buttonsOptions: {
                enabled: false,
            },
            keyExpr: ActionService.TreeKeyFieldName,
            parentIdExpr: ActionService.TreeParentFieldName,
            addCustomFilters: true,
            localStorageVarForSavedEntity: "actionDetailedParamGrid",
            allowColumnReordering: !this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.ActionParameters),
            columns: this.actionService.getActionDetailedParametersGridColumns(),
            dataSource: this.actionService.createDataSourceForExtendedParameters(this.referenceObject),

            onInitialized: (e: any, grid: TreeListController<OpAction>) => {
                this.detailedParamGrid = grid
            },
            onDisposed: () => {
                this.detailedParamGrid = null
            }
        }
    }

    private updatePacketData(): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.PACKET_LIST)) {
            this.$scope.isPackedDataLoading = true

            this.actionService
                .getActionPackets(this.referenceObject.idAction)
                .then((packets: OpActionPacket[]) => {
                    return this.updatePacketsData(packets)
                })
                .always(() => {
                    this.$scope.isPackedDataLoading = false
                    def.resolve()
                })
        } else {
            def.resolve()
        }

        return def.promise()
    }

    private setTemplateConsts(): void {
        this.$scope.ACTION_COMMON_TAB_NAME = this.ACTION_COMMON_TAB_NAME
        this.$scope.ACTION_HISTORY_TAB_NAME = this.ACTION_HISTORY_TAB_NAME
        this.$scope.ACTION_PARAMS_ACCORDION_NAME = this.ACTION_PARAMS_ACCORDION_NAME
        this.$scope.ACTION_ADDITIONAL_PARAMS_ACCORDION_NAME = this.ACTION_ADDITIONAL_PARAMS_ACCORDION_NAME
        this.$scope.ACTION_PACKETS_ACCORDION_NAME = this.ACTION_PACKETS_ACCORDION_NAME
        this.$scope.NO_ACTIVE_ACCORDIONS_NAME = this.NO_ACTIVE_ACCORDIONS_NAME
        this.$scope.EDIT_NAME = this.EDIT_NAME
        this.$scope.NO_DATA_TO_SHOW = this.NO_DATA_TO_SHOW
    }

    // Options End

    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: OpAction) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}